import React, { useState, useEffect } from 'react';
import {
  getTeamMappings,
  updateTeamMapping,
  syncTeamsFromMatches,
  getLeagueMappings,
  updateLeaguePsynetMapping,
  TeamMapping,
  LeagueMapping
} from '../api';
import { useSports } from '../hooks';
import { Pagination, Button } from './common';
import { toast } from 'react-toastify';
import { commonStyles, colors, typography, spacing } from '../styles/design-system';
import { User } from '../api/auth';

interface PsynetManagerProps {
  currentUser: User;
}

const PsynetManager: React.FC<PsynetManagerProps> = ({ currentUser }) => {
  const [subTab, setSubTab] = useState<'teams' | 'leagues'>('teams');
  const [loading, setLoading] = useState(false);

  // 종목 데이터 - useSports 훅 사용
  const { sports, sportOptions, getSportLabelById, getSportLabelByName } = useSports();

  // 팀 관리 상태
  const [teamMappings, setTeamMappings] = useState<TeamMapping[]>([]);
  const [teamFilter, setTeamFilter] = useState({ sport: '' });
  const [editingTeamId, setEditingTeamId] = useState<number | null>(null);
  const [editingTeamPsynetName, setEditingTeamPsynetName] = useState('');
  const [teamPage, setTeamPage] = useState(1);

  // 리그 관리 상태
  const [leagueMappings, setLeagueMappings] = useState<LeagueMapping[]>([]);
  const [leagueFilter, setLeagueFilter] = useState({ sport: '' });
  const [editingLeagueCode, setEditingLeagueCode] = useState<string | null>(null);
  const [editingLeaguePsynetId, setEditingLeaguePsynetId] = useState('');
  const [leaguePage, setLeaguePage] = useState(1);

  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    if (subTab === 'teams') {
      loadTeamMappings();
    } else if (subTab === 'leagues') {
      loadLeagueMappings();
    }
  }, [subTab]);

  // 팀 관리 탭 최초 진입 시 조용히 팀 동기화 실행
  useEffect(() => {
    if (subTab === 'teams') {
      const silentSyncTeams = async () => {
        try {
          await syncTeamsFromMatches();
          loadTeamMappings();
        } catch (error) {
          console.error('Silent team sync failed:', error);
        }
      };
      silentSyncTeams();
    }
  }, [subTab]);

  useEffect(() => {
    if (subTab === 'teams') {
      loadTeamMappings();
    }
  }, [teamFilter.sport]);

  useEffect(() => {
    if (subTab === 'leagues') {
      loadLeagueMappings();
    }
  }, [leagueFilter.sport]);

  const loadTeamMappings = async () => {
    setLoading(true);
    try {
      const result = await getTeamMappings(
        teamFilter.sport || undefined,
        undefined
      );
      setTeamMappings(result.data || []);
      setTeamPage(1);
    } catch (error) {
      console.error('Failed to load team mappings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLeagueMappings = async () => {
    setLoading(true);
    try {
      const result = await getLeagueMappings(leagueFilter.sport || undefined);
      setLeagueMappings(result.data || []);
      setLeaguePage(1);
    } catch (error) {
      console.error('Failed to load league mappings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTeamPsynetName = async (teamId: number) => {
    try {
      await updateTeamMapping(teamId, { psynet_name: editingTeamPsynetName || undefined });
      toast.success('팀 Psynet 이름이 저장되었음.');
      setEditingTeamId(null);
      setEditingTeamPsynetName('');
      loadTeamMappings();
    } catch (error: any) {
      console.error('Failed to update team mapping:', error);
      toast.error(`저장 실패: ${error.message}`);
    }
  };

  const handleSaveLeaguePsynetId = async (leagueCode: string) => {
    try {
      await updateLeaguePsynetMapping(leagueCode, editingLeaguePsynetId);
      toast.success('리그 Psynet ID가 저장되었음.');
      setEditingLeagueCode(null);
      setEditingLeaguePsynetId('');
      loadLeagueMappings();
    } catch (error: any) {
      console.error('Failed to update league mapping:', error);
      toast.error(`저장 실패: ${error.message}`);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.subTabContainer}>
        <button
          style={{...styles.subTabButton, ...(subTab === 'teams' ? styles.activeSubTab : {})}}
          onClick={() => setSubTab('teams')}
        >
          팀 관리
        </button>
        <button
          style={{...styles.subTabButton, ...(subTab === 'leagues' ? styles.activeSubTab : {})}}
          onClick={() => setSubTab('leagues')}
        >
          리그 관리
        </button>
      </div>

      {/* 팀 관리 탭 */}
      {subTab === 'teams' && (
        <>
          <div style={styles.filterRow}>
            <select
              style={styles.selectFilter}
              value={teamFilter.sport}
              onChange={(e) => { setTeamFilter({...teamFilter, sport: e.target.value}); }}
            >
              <option value="">전체 종목</option>
              {sportOptions.map(sport => (
                <option key={sport.value} value={sport.value}>{sport.label}</option>
              ))}
            </select>
          </div>
          <div style={styles.tableContainerFixed}>
            <table style={{...styles.table, marginBottom: 0}}>
              <thead style={styles.stickyHead}>
                <tr style={styles.tableHeaderRow}>
                  <th style={{...styles.th, width: '70px'}}>종목</th>
                  <th style={{...styles.th, width: '35%'}}>팀명</th>
                  <th style={{...styles.th, width: '35%'}}>Psynet 이름</th>
                  <th style={{...styles.th, width: '80px'}}>관리</th>
                </tr>
              </thead>
              <tbody>
                {teamMappings.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={styles.emptyCell}>
                      {loading ? '로딩 중...' : '등록된 팀이 없음.'}
                    </td>
                  </tr>
                ) : (
                  teamMappings.slice((teamPage - 1) * ITEMS_PER_PAGE, teamPage * ITEMS_PER_PAGE).map((team) => (
                    <tr key={team.team_id} style={styles.tableRow}>
                      <td style={styles.td}>{team.sport_name_kr || getSportLabelByName(team.sport_name)}</td>
                      <td style={styles.td}>{team.team_name}</td>
                      <td style={styles.td}>
                        {editingTeamId === team.team_id ? (
                          <input
                            type="text"
                            value={editingTeamPsynetName}
                            onChange={(e) => setEditingTeamPsynetName(e.target.value)}
                            style={styles.inputCompact}
                            placeholder="Psynet 팀명"
                          />
                        ) : (
                          team.psynet_name || <span style={styles.emptyValue}>-</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        {editingTeamId === team.team_id ? (
                          <div style={styles.buttonGroup}>
                            <Button variant="primary" size="sm" onClick={() => handleSaveTeamPsynetName(team.team_id)}>저장</Button>
                            <Button variant="secondary" size="sm" onClick={() => { setEditingTeamId(null); setEditingTeamPsynetName(''); }}>취소</Button>
                          </div>
                        ) : (
                          <Button variant="secondary" size="sm" onClick={() => { setEditingTeamId(team.team_id); setEditingTeamPsynetName(team.psynet_name || ''); }}>수정</Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            pagination={{
              currentPage: teamPage,
              totalPages: Math.ceil(teamMappings.length / ITEMS_PER_PAGE),
              totalItems: teamMappings.length
            }}
            onPageChange={setTeamPage}
            loading={loading}
            style={{ marginTop: spacing[3] }}
          />
        </>
      )}

      {/* 리그 관리 탭 */}
      {subTab === 'leagues' && (
        <>
          <div style={styles.filterRow}>
            <select
              style={styles.selectFilter}
              value={leagueFilter.sport}
              onChange={(e) => setLeagueFilter({...leagueFilter, sport: e.target.value})}
            >
              <option value="">전체 종목</option>
              {sportOptions.map(sport => (
                <option key={sport.value} value={sport.value}>{sport.label}</option>
              ))}
            </select>
          </div>
          <div style={styles.tableContainerFixed}>
            <table style={{...styles.table, marginBottom: 0}}>
              <thead style={styles.stickyHead}>
                <tr style={styles.tableHeaderRow}>
                  <th style={{...styles.th, width: '70px'}}>종목</th>
                  <th style={{...styles.th, width: '40%'}}>리그명</th>
                  <th style={{...styles.th, width: '35%'}}>Psynet 리그 ID</th>
                  <th style={{...styles.th, width: '80px'}}>관리</th>
                </tr>
              </thead>
              <tbody>
                {leagueMappings.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={styles.emptyCell}>
                      {loading ? '로딩 중...' : '등록된 리그가 없음.'}
                    </td>
                  </tr>
                ) : (
                  leagueMappings.slice((leaguePage - 1) * ITEMS_PER_PAGE, leaguePage * ITEMS_PER_PAGE).map((league) => (
                    <tr key={league.league_id} style={styles.tableRow}>
                      <td style={styles.td}>{league.sport_name_kr || getSportLabelById(league.sport_id) || '-'}</td>
                      <td style={styles.td}>{league.league_name}</td>
                      <td style={styles.td}>
                        {editingLeagueCode === league.league_code ? (
                          <input
                            type="text"
                            value={editingLeaguePsynetId}
                            onChange={(e) => setEditingLeaguePsynetId(e.target.value)}
                            style={styles.inputCompact}
                            placeholder="예: OT28, 11"
                          />
                        ) : (
                          league.psynet_league_id || <span style={styles.emptyValue}>-</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        {editingLeagueCode === league.league_code ? (
                          <div style={styles.buttonGroup}>
                            <Button variant="primary" size="sm" onClick={() => handleSaveLeaguePsynetId(league.league_code)}>저장</Button>
                            <Button variant="secondary" size="sm" onClick={() => { setEditingLeagueCode(null); setEditingLeaguePsynetId(''); }}>취소</Button>
                          </div>
                        ) : (
                          <Button variant="secondary" size="sm" onClick={() => { setEditingLeagueCode(league.league_code); setEditingLeaguePsynetId(league.psynet_league_id || ''); }}>수정</Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            pagination={{
              currentPage: leaguePage,
              totalPages: Math.ceil(leagueMappings.length / ITEMS_PER_PAGE),
              totalItems: leagueMappings.length
            }}
            onPageChange={setLeaguePage}
            loading={loading}
            style={{ marginTop: spacing[3] }}
          />
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing[5],
    paddingBottom: spacing[10],
  },
  subTabContainer: commonStyles.tabContainer,
  subTabButton: {
    ...commonStyles.tabButton,
    padding: `${spacing[3]} ${spacing[6]}`,
    fontSize: typography.fontSize.base,
  },
  activeSubTab: commonStyles.tabButtonActive,
  selectCompact: {
    ...commonStyles.select,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
    minWidth: '80px',
  },
  tableContainerFixed: {
    height: '420px',
    overflow: 'auto',
    border: `1px solid ${colors.gray[200]}`,
    borderRadius: '4px',
  },
  table: commonStyles.table,
  th: commonStyles.th,
  td: commonStyles.td,
  tableHeaderRow: {
    backgroundColor: colors.gray[100],
  },
  tableRow: {
    borderBottom: `1px solid ${colors.gray[200]}`,
  },
  inputCompact: {
    ...commonStyles.input,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
  },
  filterRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing[3],
  },
  selectFilter: {
    ...commonStyles.select,
    padding: `${spacing[1]} ${spacing[3]}`,
    fontSize: typography.fontSize.sm,
    minWidth: '120px',
  },
  stickyHead: {
    position: 'sticky' as const,
    top: 0,
    zIndex: 1,
  },
  emptyCell: {
    ...commonStyles.td,
    textAlign: 'center' as const,
    padding: '40px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '4px',
  },
  emptyValue: {
    color: colors.gray[400],
  },
};

export default PsynetManager;
